import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cpu, User } from 'lucide-react';
import { Change } from '@the-real-insight/tri-model';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { currentLocale } from '../components/numericFormat';
import { ValueConfidence } from './valueConfidence';
import { EntityLink } from '../entities/EntityLink';

/**
 * What a field's value is worth, and where it came from.
 *
 * A value that was estimated looks exactly like one that was typed — the whole reason the activation
 * records *how* it arrived at a number. A history icon made that visible only where a history
 * existed, which is the wrong half: the field with no history and no value is the one most worth
 * marking, and it showed nothing at all.
 *
 * So the trigger is a coloured dot that is always there — green, amber or red per
 * `valueConfidence` — and opening it gives the reasoning behind the colour. Always there also keeps
 * the column of labels from shifting, which is what the eye runs down.
 *
 * Going back to an earlier value writes it forward as a new change rather than deleting the ones
 * after it. History that can be rewritten is not history, and the question "who put this back and
 * when" is exactly the one someone will ask.
 */

/**
 * The model's own `Change`, not a hand-written copy of it.
 *
 * The copy this replaced called the timestamp `date`, which the model calls `timestamp` — so every
 * entry rendered without one and nobody could tell when anything had happened. A re-declared shape
 * agrees with the real one only until someone looks away.
 */
export type FieldChange = Change;

/**
 * Who wrote a value, by colour as well as by icon: a person and a computation are the two things a
 * reader is trying to tell apart. Both hues are the ones the page has always used — a fixed pair
 * rather than theme tokens, because their whole job is to stay distinguishable from each other.
 */
const WHO_COLOR = {
    user: 'text-[#1F5C8B]',
    system: 'text-[#A8751A]',
} as const;

/**
 * One colour per state, and only three of them.
 *
 * Fixed hues rather than theme tokens for the same reason the two author colours are fixed: their
 * whole job is to stay apart from each other, and a palette that shifts with a theme would
 * eventually put amber and red within a shade of one another.
 */
const CONFIDENCE_COLOR: Record<ValueConfidence, string> = {
    certain: 'bg-[#2F8F4E]',
    estimated: 'bg-[#D08A17]',
    missing: 'bg-[#C0392B]',
};

interface Properties {
    changes: FieldChange[];
    /** Green, amber or red — see `valueConfidence`. */
    confidence: ValueConfidence;
    /** How a stored value is written for the reader. */
    format: (value: any) => string;
    /** Absent where nothing may be written back — a tile shows the history, it does not edit. */
    onRestore?: (value: any) => void;
    disabled?: boolean;
}

export default function FieldHistory({ changes, confidence, format, onRestore, disabled }: Properties) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    // Newest first: the interesting end of a history is the recent one.
    const ordered = [...changes].reverse();
    const explanation = t(`portfolioDashboard.confidence.${confidence}`);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    // Its own element rather than an icon button: what is being pressed is the dot,
                    // and a 36 px button around an 8 px dot puts the click target somewhere the eye
                    // does not see anything.
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors hover:bg-accent"
                    title={explanation}
                    aria-label={explanation}
                >
                    <span className={cn('block h-2.5 w-2.5 rounded-full', CONFIDENCE_COLOR[confidence])} />
                </button>
            </PopoverTrigger>

            <PopoverContent align="start" className="max-h-[420px] w-auto max-w-[340px] overflow-y-auto p-3">
                {/* The colour said in words: a dot is quick to read and impossible to look up. */}
                <div className="mb-2 flex items-center gap-2">
                    <span className={cn('block h-2.5 w-2.5 shrink-0 rounded-full', CONFIDENCE_COLOR[confidence])} />
                    <span className="text-sm font-semibold">{explanation}</span>
                </div>

                <div className="mb-2 text-xs font-bold uppercase tracking-[0.07em] text-muted-foreground">
                    {t('portfolioDashboard.history.title')}
                </div>

                {ordered.length === 0 ? (
                    <div className="text-xs text-muted-foreground">
                        {t('portfolioDashboard.history.empty')}
                    </div>
                ) : (
                    <></>
                )}

                {ordered.map((change, index) => (
                    <div
                        key={`${String(change.timestamp ?? '')}-${index}`}
                        className="border-t py-2 first-of-type:border-t-0"
                    >
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="text-sm font-bold">{format(change.value)}</span>
                            <span
                                className={cn(
                                    'inline-flex items-center gap-1 whitespace-nowrap text-xs',
                                    change.type === 'system' ? WHO_COLOR.system : WHO_COLOR.user,
                                )}
                            >
                                {/* The icon is what separates the two at a glance; the
                                    wording stays because an e-mail says more than a shape. */}
                                {change.type === 'system' ? (
                                    <Cpu size={12} strokeWidth={1.9} />
                                ) : (
                                    <User size={12} strokeWidth={1.9} />
                                )}
                                {change.type === 'system'
                                    ? t('portfolioDashboard.history.system')
                                    : change.user || t('portfolioDashboard.history.user')}
                            </span>
                        </div>

                        {change.timestamp ? (
                            <div className="text-xs text-muted-foreground">
                                {new Date(change.timestamp).toLocaleString(currentLocale())}
                            </div>
                        ) : (
                            <></>
                        )}

                        {change.comment ? <div className="mt-1 text-xs">{change.comment}</div> : <></>}

                        {/* Der Wert kam von einer Entität — ein Dokument hat sie geliefert, der Slot hängt jetzt an ihr. */}
                        {(change as any).reference?._id ? (
                            <div className="mt-1 text-xs">
                                <EntityLink reference={(change as any).reference} label={t('portfolioDashboard.slots.historyLink')} />
                            </div>
                        ) : (
                            <></>
                        )}

                        {/* The newest entry is the current value; there is nothing to go back to. */}
                        {index > 0 && onRestore ? (
                            // A link, not a filled button: this sits inside a dense list of
                            // entries, and a solid button per row would outweigh the entries it
                            // belongs to.
                            <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="px-0 underline"
                                disabled={disabled}
                                onClick={() => {
                                    onRestore(change.value);
                                    setOpen(false);
                                }}
                            >
                                {t('portfolioDashboard.history.restore')}
                            </Button>
                        ) : (
                            <></>
                        )}
                    </div>
                ))}
            </PopoverContent>
        </Popover>
    );
}
